import {
  type Dispatch,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type SetStateAction,
  type TouchEvent as ReactTouchEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { Button, cx, EmptyState, ErrorState, panelSurface, SegmentedControl, Skeleton, SkeletonRegion } from "@akaike/ui";
import {
  type Column,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnSizingState,
  type Header,
  type Row,
  type RowData,
  type SortingState,
  type Table,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";

// A headless, virtualized data table on TanStack Table v8. Renders a div-grid
// (not a <table>) so it can virtualize rows, keep a sticky header, and pin the
// first column while staying pixel-aligned. Styled by the `.dt` block in
// qcs.css to mirror the look of the hand-rolled `.tbl`.
//
// Persistence split (per the migration plan): sort + filters + global search
// live in the URL (shareable, refresh-safe); column visibility is a personal
// preference kept in localStorage. Row height is a fixed constant so
// virtualization needs no per-row measurement.

// Per-column knobs, read off `column.columnDef.meta`.
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    // Horizontal alignment; maps to the same `.r`/`.c` helpers as `.tbl`.
    align?: "left" | "center" | "right";
    // Extra class(es) on the cell wrapper (e.g. "strong", "num").
    cellClassName?: string;
    // Base pixel width; columns grow unless flex is false.
    width?: number;
    // Disable proportional growth while retaining manual column resizing.
    flex?: boolean;
    // Minimum width for a flexible column (default 140).
    minWidth?: number;
    // Render this column as a faceted <select> filter in the toolbar.
    filterSelect?: boolean;
    // Fixed choices for a segmented facet; an empty value clears the filter.
    filterSegments?: readonly { value: string; label: string }[];
    // Human label for the faceted filter (defaults to the header text).
    filterLabel?: string;
    // Value used for CSV export (defaults to the raw cell value).
    exportValue?: (row: TData) => string | number | null | undefined;
    // Text the global search matches this column against (defaults to the cell
    // value). Use it when the cell shows more than the accessor value -- e.g. a
    // name with the id underneath, so typing either one finds the row.
    searchValue?: (row: TData) => string;
  }
}


// Global search: substring match, but against `meta.searchValue` when a column
// defines one (so a "Name / id" cell matches on either half).
function globalSearchFilter<T>(row: Row<T>, columnId: string, filterValue: unknown): boolean {
  const needle = String(filterValue ?? "").toLowerCase();
  if (!needle) return true;
  const meta = row.getAllCells().find((c) => c.column.id === columnId)?.column.columnDef.meta;
  const hay = meta?.searchValue ? meta.searchValue(row.original) : row.getValue(columnId);
  return String(hay ?? "").toLowerCase().includes(needle);
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// localStorage-backed state (personal prefs: column sizing + order). Reads once
// on mount; writes on change. Silently degrades if storage is unavailable.
function usePersisted<T>(key: string, fallback: T): [T, Dispatch<SetStateAction<T>>] {
  const [v, setV] = useState<T>(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) return JSON.parse(saved) as T;
    } catch {
      // ignore malformed/unavailable storage
    }
    return fallback;
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(v));
    } catch {
      // ignore
    }
  }, [key, v]);
  return [v, setV];
}

// -- URL <-> table state (namespaced by tableKey) ----------------------------
// Keys: `${k}.sort` = "colId:desc", `${k}.q` = global text,
// `${k}.f.<colId>` = a column filter value.
function readUrlState(params: URLSearchParams, k: string) {
  const sorting: SortingState = [];
  const sortRaw = params.get(`${k}.sort`);
  if (sortRaw) {
    const [id, dir] = sortRaw.split(":");
    if (id) sorting.push({ id, desc: dir === "desc" });
  }
  const columnFilters: ColumnFiltersState = [];
  const prefix = `${k}.f.`;
  for (const [key, val] of params.entries()) {
    if (key.startsWith(prefix) && val) {
      columnFilters.push({ id: key.slice(prefix.length), value: val });
    }
  }
  const globalFilter = params.get(`${k}.q`) ?? "";
  return { sorting, columnFilters, globalFilter };
}

// Mixing accessor columns (typed value) and display columns in one array yields
// a union TS can't reconcile against a single value type, so the column value
// generic is `any` here, the standard TanStack pattern.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyColumnDef<T> = ColumnDef<T, any>;

export type DataTableProps<T> = {
  columns: AnyColumnDef<T>[];
  data: T[];
  getRowId: (row: T, index: number) => string;
  // Namespace for URL params + localStorage; must be unique per table on a page.
  tableKey: string;
  globalPlaceholder?: string;
  enableGlobalFilter?: boolean;
  enableColumnVisibility?: boolean;
  enableCsvExport?: boolean;
  showRowCount?: boolean;
  csvFilename?: string;
  pinFirstColumn?: boolean;
  // Column ids hidden by default (user can re-show; choice persists).
  defaultHidden?: string[];
  // Hard cap for the scroll viewport height in px (default 560). Ignored when
  // `fillHeight` grows the table to the bottom of the window.
  maxBodyHeight?: number;
  // Reserve the scroll viewport through loading, empty results and filtering.
  fixedBodyHeight?: boolean;
  // Uniform row height, including the virtual scroll estimate.
  rowHeight?: number;
  // When true (use on single-dominant-table pages), the scroll viewport grows to
  // fill the remaining window height instead of a fixed 560px cap, so tall screens
  // show far more rows without a cramped internal scroll. maxHeight still means the
  // table only takes what its rows need when there are few.
  fillHeight?: boolean;
  rowClassName?: (row: T) => string | undefined;
  onFilteredRowsChange?: (rows: T[]) => void;
  emptyMessage?: string;
  errorMessage?: string;
  isLoading?: boolean;
  // Extra toolbar content, right-aligned before the built-in controls.
  toolbarExtras?: ReactNode;
};

export function DataTable<T>({
  columns,
  data,
  getRowId,
  tableKey,
  globalPlaceholder = "Search…",
  enableGlobalFilter = true,
  enableColumnVisibility = true,
  enableCsvExport = true,
  showRowCount = true,
  csvFilename,
  pinFirstColumn = false,
  defaultHidden = [],
  maxBodyHeight = 560,
  fixedBodyHeight = false,
  rowHeight = 36,
  fillHeight = false,
  rowClassName,
  onFilteredRowsChange,
  emptyMessage = "No rows.",
  errorMessage,
  isLoading = false,
  toolbarExtras,
}: DataTableProps<T>) {
  const [params, setParams] = useSearchParams();

  // Initialise from the URL once; thereafter this component owns the state and
  // mirrors it back to the URL.
  const initial = useMemo(() => readUrlState(params, tableKey), [tableKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sorting, setSorting] = useState<SortingState>(initial.sorting);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initial.columnFilters);
  const [globalRaw, setGlobalRaw] = useState(initial.globalFilter);
  const globalFilter = useDebounced(globalRaw, 150);

  const visKey = `dt.vis.${tableKey}`;
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    const base: VisibilityState = {};
    for (const id of defaultHidden) base[id] = false;
    try {
      const saved = localStorage.getItem(visKey);
      if (saved) Object.assign(base, JSON.parse(saved));
    } catch {
      // ignore malformed persisted state
    }
    return base;
  });
  useEffect(() => {
    try {
      localStorage.setItem(visKey, JSON.stringify(columnVisibility));
    } catch {
      // storage may be unavailable (private mode); non-fatal
    }
  }, [visKey, columnVisibility]);

  // Column widths + order are personal prefs too (localStorage, not the URL).
  const [columnSizing, setColumnSizing] = usePersisted<ColumnSizingState>(`dt.size.${tableKey}`, {});
  const [columnOrder, setColumnOrder] = usePersisted<ColumnOrderState>(`dt.order.${tableKey}`, []);

  // Push sort / filters / global search into the URL (visibility stays local).
  useEffect(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        // Clear our namespace first. The array is a deliberate snapshot: the loop
        // deletes from `next`, and URLSearchParams' key iterator is index-based,
        // so iterating it live skips the entry after each removal and a middle
        // filter key survives into the URL. unicorn/no-useless-spread cannot see
        // the mutation and calls the array redundant; it is wrong here.
        // oxlint-disable-next-line unicorn/no-useless-spread
        for (const key of [...next.keys()]) {
          if (key === `${tableKey}.sort` || key === `${tableKey}.q` || key.startsWith(`${tableKey}.f.`)) {
            next.delete(key);
          }
        }
        if (sorting[0]) next.set(`${tableKey}.sort`, `${sorting[0].id}:${sorting[0].desc ? "desc" : "asc"}`);
        if (globalFilter) next.set(`${tableKey}.q`, globalFilter);
        for (const f of columnFilters) {
          if (f.value) next.set(`${tableKey}.f.${f.id}`, String(f.value));
        }
        return next;
      },
      { replace: true },
    );
  }, [sorting, columnFilters, globalFilter, tableKey, setParams]);

  // Seed each column's default width from its meta (so resizing has a sane
  // starting point and the grid template is definite).
  const sizedColumns = useMemo(
    () =>
      columns.map((c) => {
        const meta = c.meta;
        const size = c.size ?? meta?.width ?? meta?.minWidth ?? 180;
        const out = { minSize: 56, ...c, size };
        // Faceted <select> filters pick an exact category, so match exactly (not
        // the default substring match) unless the column already sets a filterFn.
        if (meta?.filterSelect && out.filterFn == null) out.filterFn = "equalsString";
        return out;
      }),
    [columns],
  );

  const table = useReactTable({
    data,
    columns: sizedColumns,
    getRowId,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      columnSizing,
      columnOrder,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onColumnOrderChange: setColumnOrder,
    columnResizeMode: "onChange",
    enableColumnResizing: true,
    globalFilterFn: globalSearchFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    enableGlobalFilter,
  });

  const sortedRows = table.getRowModel().rows;

  // Surface the filtered+sorted rows upward (used by Stores to sync the map).
  const filtered = useMemo(() => sortedRows.map((r) => r.original), [sortedRows]);
  useEffect(() => {
    onFilteredRowsChange?.(filtered);
  }, [filtered, onFilteredRowsChange]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: sortedRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  });

  // fillHeight: measure the space from the scroll viewport's top to the bottom of
  // the window and use it as the maxHeight, so the table fills tall screens instead
  // of wasting the lower half. Re-measures on resize. Floors at 280px.
  const [fillMax, setFillMax] = useState<number | null>(null);
  useEffect(() => {
    if (!fillHeight) return;
    const measure = () => {
      const el = scrollRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setFillMax(Math.floor(window.innerHeight - top - 24));
    };
    measure();
    window.addEventListener("resize", measure);
    // Re-measure when content above the table reflows (async cards/filters loading,
    // sidebar collapse, wrapping toolbar) so `top` stays accurate, not just on resize.
    const ro = new ResizeObserver(measure);
    if (typeof document !== "undefined") ro.observe(document.body);
    return () => {
      window.removeEventListener("resize", measure);
      ro.disconnect();
    };
  }, [fillHeight]);
  // Fill the space from the table's top to the window bottom (floored so it never
  // gets uselessly small), so it grows on tall screens and stays within short ones
  // instead of overflowing. Before the first measure, fall back to the fixed default.
  const bodyMaxHeight = fillHeight ? Math.max(280, fillMax ?? maxBodyHeight) : maxBodyHeight;

  const visibleCols = table.getVisibleLeafColumns();
  // Columns flex to fill the available width so the table uses the horizontal space
  // instead of leaving dead space on the right (and so more columns fit before a
  // horizontal scroll appears). Each flexible column grows from its configured size
  // (weighted by that size, so wider columns take proportionally more), but never
  // shrinks below it. Small action/icon columns (<=72px) and user-resized columns
  // stay fixed. When the flexed minimums still exceed the container, it scrolls.
  const gridTemplate = visibleCols
    .map((c) => {
      const size = c.getSize();
      const resized = columnSizing[c.id] != null;
      if (resized || c.columnDef.meta?.flex === false || size <= 72) return `${size}px`;
      const weight = Math.max(1, Math.round(size / 40));
      return `minmax(${size}px, ${weight}fr)`;
    })
    .join(" ");
  const headers = table.getHeaderGroups()[0]?.headers ?? [];

  // Start a column resize. Flexible columns render wider than their base size (via
  // `fr`), so first freeze every visible column to its current rendered width; this
  // pins the layout (synchronously, so the resize handler reads the frozen start
  // size) and prevents the grabbed column from snapping to its base width.
  const beginResize =
    (header: Header<T, unknown>) => (e: ReactMouseEvent | ReactTouchEvent) => {
      const headEl = scrollRef.current?.querySelector(".dt-head");
      if (headEl) {
        const snapshot: ColumnSizingState = {};
        visibleCols.forEach((c, i) => {
          const cell = headEl.children[i] as HTMLElement | undefined;
          if (cell) snapshot[c.id] = Math.round(cell.getBoundingClientRect().width);
        });
        flushSync(() => setColumnSizing((prev) => ({ ...prev, ...snapshot })));
      }
      header.getResizeHandler()(e);
    };

  const facetCols = table.getAllLeafColumns().filter((c) => c.columnDef.meta?.filterSelect);

  // Drag-to-reorder: track the column being dragged; on drop, splice it in front
  // of the drop target within the persisted column order.
  const [dragCol, setDragCol] = useState<string | null>(null);
  const reorder = (from: string, to: string) => {
    const current = table.getAllLeafColumns().map((c) => c.id);
    const base = columnOrder.length ? [...columnOrder] : current;
    for (const id of current) if (!base.includes(id)) base.push(id);
    const fromIdx = base.indexOf(from);
    const toIdx = base.indexOf(to);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = base.splice(fromIdx, 1);
    // removing the source shifts a right-of-source target down by one; insert in
    // front of the drop target either way.
    base.splice(fromIdx < toIdx ? toIdx - 1 : toIdx, 0, moved);
    setColumnOrder(base);
  };

  return (
    <div className="dt">
      <div className="dt-toolbar">
        {enableGlobalFilter && (
          <div className="dt-search">
            <svg
              className="dt-search-ic"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.3-4.3" />
            </svg>
            <input
              className="dt-search-input"
              placeholder={globalPlaceholder}
              value={globalRaw}
              onChange={(e) => setGlobalRaw(e.currentTarget.value)}
            />
            {globalRaw && (
              <button
                className="dt-search-clear"
                onClick={() => setGlobalRaw("")}
                aria-label="clear search"
                title="Clear"
              >
                ✕
              </button>
            )}
          </div>
        )}
        {facetCols.map((c) => (
          <FacetSelect key={c.id} column={c} />
        ))}
        {toolbarExtras}
        <div className="flex-auto" />
        {showRowCount && <span className="dt-count">
          {sortedRows.length === data.length
            ? `${sortedRows.length}`
            : `${sortedRows.length} of ${data.length}`}
        </span>}
        {enableColumnVisibility && <ColumnMenu table={table} />}
        {enableCsvExport && (
          <Button
            variant="outline"
            size="toolbar"
            onClick={() => exportCsv(sortedRows, visibleCols, csvFilename ?? tableKey)}
            title="Export the current filtered + sorted view to CSV"
          >
            Export CSV
          </Button>
        )}
      </div>

      <div className={cx("dt-scroll", fixedBodyHeight && sortedRows.length === 0 && "flex flex-col")} ref={scrollRef} style={{ maxHeight: bodyMaxHeight, height: fixedBodyHeight ? bodyMaxHeight : undefined }} aria-busy={isLoading}>
        <div className="dt-grid shrink-0">
          <div className="dt-head" style={{ gridTemplateColumns: gridTemplate }}>
            {headers.map((header: Header<T, unknown>, i) => {
              const col = header.column;
              const meta = col.columnDef.meta;
              const align = meta?.align;
              const canSort = col.getCanSort();
              const sorted = col.getIsSorted();
              const pinned = pinFirstColumn && i === 0;
              return (
                <div
                  key={col.id}
                  className={cx(
                    "dt-h",
                    align === "right" && "r",
                    align === "center" && "c",
                    pinned && "dt-pin",
                    dragCol === col.id && "dragging",
                  )}
                  onDragOver={(e) => {
                    if (dragCol) e.preventDefault();
                  }}
                  onDrop={() => {
                    if (dragCol && dragCol !== col.id) reorder(dragCol, col.id);
                    setDragCol(null);
                  }}
                >
                  <span
                    className={cx("dt-h-label", canSort && "sortable")}
                    draggable
                    onDragStart={() => setDragCol(col.id)}
                    onDragEnd={() => setDragCol(null)}
                    onClick={canSort ? col.getToggleSortingHandler() : undefined}
                    title={canSort ? "Click to sort · drag to reorder" : "Drag to reorder"}
                  >
                    {flexRender(col.columnDef.header, header.getContext())}
                    {canSort && (
                      <span className="dt-sort">
                        {sorted === "asc" ? "▲" : sorted === "desc" ? "▼" : "↕"}
                      </span>
                    )}
                  </span>
                  {col.getCanResize() && (
                    <div
                      className={cx("dt-resizer", col.getIsResizing() && "resizing")}
                      onMouseDown={beginResize(header)}
                      onTouchStart={beginResize(header)}
                      onClick={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.preventDefault()}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="dt-body" style={{ height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((vi) => {
              const row = sortedRows[vi.index];
              const extra = rowClassName?.(row.original);
              return (
                <div
                  key={row.id}
                  className={cx("dt-row", extra)}
                  style={{
                    gridTemplateColumns: gridTemplate,
                    height: rowHeight,
                    transform: `translateY(${vi.start}px)`,
                  }}
                >
                  {row.getVisibleCells().map((cell, i) => {
                    const meta = cell.column.columnDef.meta;
                    const align = meta?.align;
                    const pinned = pinFirstColumn && i === 0;
                    return (
                      <div
                        key={cell.id}
                        className={cx("dt-cell", align === "right" && "r", align === "center" && "c", meta?.cellClassName, pinned && "dt-pin")}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {sortedRows.length === 0 && (
          <div className={fixedBodyHeight ? "sticky left-0 flex w-full flex-1 items-center justify-center [&>div]:w-full" : undefined}>
          {isLoading ? (
            <SkeletonRegion label="Loading rows">
              <div className="flex flex-col gap-2.5 p-3.5">
                {Array.from({ length: 8 }, (_, i) => (
                  <Skeleton key={i} h={16} />
                ))}
              </div>
            </SkeletonRegion>
          ) : errorMessage ? (
            <ErrorState message={errorMessage} />
          ) : (
            <EmptyState compact title={emptyMessage} />
          )}
          </div>
        )}
      </div>
    </div>
  );
}

// -- toolbar pieces ----------------------------------------------------------

function FacetSelect<T>({ column }: { column: Column<T, unknown> }) {
  const uniq = column.getFacetedUniqueValues();
  const options = useMemo(
    () =>
      [...uniq.keys()]
        .filter((v) => v != null && v !== "")
        .sort((a, b) => String(a).localeCompare(String(b))),
    [uniq],
  );
  const label = column.columnDef.meta?.filterLabel ?? headerText(column);
  const value = (column.getFilterValue() as string) ?? "";
  const segments = column.columnDef.meta?.filterSegments;
  if (segments) return (
    <SegmentedControl
      label={`Filter by ${label}`}
      value={value}
      options={segments}
      onChange={(next) => column.setFilterValue(next || undefined)}
      className="shrink-0"
    />
  );
  return (
    <select
      className={cx("dt-facet", value && "active")}
      value={value}
      onChange={(e) => column.setFilterValue(e.currentTarget.value || undefined)}
      title={`Filter by ${label}`}
    >
      <option value="">{label}: all</option>
      {options.map((o) => (
        <option key={String(o)} value={String(o)}>
          {String(o)}
        </option>
      ))}
    </select>
  );
}

function ColumnMenu<T>({ table }: { table: Table<T> }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const cols = table.getAllLeafColumns().filter((c) => c.getCanHide());
  return (
    <div className="dt-colmenu" ref={ref}>
      <Button variant="outline" size="toolbar" onClick={() => setOpen((o) => !o)} title="Show / hide columns">
        Columns
      </Button>
      {open && (
        <div className={cx("dt-colmenu-pop", panelSurface)}>
          {cols.map((c) => (
            <label key={c.id} className="dt-colmenu-item">
              <input type="checkbox" checked={c.getIsVisible()} onChange={c.getToggleVisibilityHandler()} />
              {headerText(c)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// -- helpers -----------------------------------------------------------------

function headerText<T>(column: Column<T, unknown>): string {
  const h = column.columnDef.header;
  if (typeof h === "string") return h;
  const id = column.id;
  return id.charAt(0).toUpperCase() + id.slice(1);
}

function exportCsv<T>(rows: Row<T>[], columns: Column<T, unknown>[], filename: string) {
  const esc = (v: unknown): string => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map((c) => esc(headerText(c)));
  const body = rows.map((row) =>
    columns
      .map((c) => {
        const exp = c.columnDef.meta?.exportValue;
        if (exp) return esc(exp(row.original));
        // Use the column's accessor value (covers both accessorKey and
        // accessorFn columns); display-only columns have no accessor -> blank.
        const def = c.columnDef as {
          accessorKey?: unknown;
          accessorFn?: unknown;
        };
        if (def.accessorKey != null || def.accessorFn != null) return esc(row.getValue(c.id));
        return "";
      })
      .join(","),
  );
  const csv = [header.join(","), ...body].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export { createColumnHelper } from "@tanstack/react-table";
